import { useMelwaysLink } from '@seek/melways-react';
import { Heading, Hidden, HiddenVisually, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';

import {
  AwardsSection,
  CompanyOverviewSection,
  FeaturedSection,
  GallerySection,
  Section,
  StatementSection,
  useAppContext,
} from '@seek/libs-shared';

import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { useConfig } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { CompanyProfilesPage, ROOT_PATH } from '../../../paths';
import { useAuth } from '../../../shared/providers/authProvider';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';
import { CompaniesList } from '../../components/companiesList/CompaniesList';
import { ReviewsSummarySectionWithDetails } from '../../components/companyReviews/reviewsSummarySectionWithDetails/ReviewsSummarySectionWithDetails';
import { SeoCtaBlurb } from '../../components/seoCtaBlurb/SeoCtaBlurb';
import translations from './.vocab';
import type { AboutModel } from './aboutModel';

interface AboutPageProps {
  model: AboutModel;
  trackRandomCompanyProfileLinkClicked: ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => void;
  trackWriteAReviewClicked: () => void;
  trackReviewsClicked: () => void;
  trackSeeAllPhotosButtonClicked: () => void;
  trackWebsiteClicked: () => void;
  onActiveTabChanged: (item: string | undefined) => void;
  getCompanyProfilesPageUrl: (p: CompanyProfilesPage) => string;
}

export function AboutPage({
  model,
  trackRandomCompanyProfileLinkClicked,
  trackWriteAReviewClicked,
  trackReviewsClicked,
  trackSeeAllPhotosButtonClicked,
  trackWebsiteClicked,
  onActiveTabChanged,
  getCompanyProfilesPageUrl,
}: AboutPageProps) {
  const config = useConfig();
  const { language } = config;
  const { companyId, companyName, ...context } = useAppContext();
  const urlParams = useParams();
  const localize = useMelwaysLink();
  const { authenticationStatus } = useAuth();
  const [params] = useSearchParams();
  const { t } = useTranslations(translations);
  const isDraftLink = params.get('draft') === 'true' ? '?draft=true' : '';

  const shouldDisplayAiGenSection =
    config.zone !== 'asia-1' && // Feature is not available in Asia-1 (Hong Kong)
    config.zone !== 'asia-3' && // Feature is not available in Asia-3 (Thailand)
    Boolean(
      model.companyReviewsAISummary?.aISummaryContent
        ?.positiveAndChallengesSummary,
    ); //Show only where there is AI summary content;

  useEffect(() => {
    if (
      companyId &&
      companyName &&
      authenticationStatus !== AUTHENTICATION_TYPES.AUTH_PENDING
    )
      trackViewEvents.aboutPage({
        config,
        context: { ...context, companyId, companyName },
        isAiReviewSummaryShown: shouldDisplayAiGenSection,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companyName, authenticationStatus]);

  const shouldDisplayOverviewSection = Boolean(
    model.overview &&
      Object.keys(model.overview).length > 0 &&
      Object.values(model.overview).some(Boolean),
  );

  const shouldDisplayFeaturedSection = Boolean(
    model.featured && Object.keys(model.featured).length > 0,
  );

  const shouldDisplayAwardsSection = Boolean(
    model.awards && Object.keys(model.awards).length > 0,
  );

  const seeAllReviewsLink = localize({
    path: `/${ROOT_PATH}/${urlParams.companySlug}/${CompanyProfilesPage.Reviews}${isDraftLink}`,
    language,
  });

  return (
    <>
      <HiddenVisually>
        <Heading level="1">
          {t('Company name Company Profile', {
            companyName,
          })}
        </Heading>
      </HiddenVisually>
      <Stack space={['none', 'gutter']}>
        {shouldDisplayOverviewSection && (
          <CompanyOverviewSection
            mode="view"
            model={{ ...model.overview, name: model.name }}
            trackWebsiteClicked={trackWebsiteClicked}
          />
        )}
        {model.missionStatement?.description &&
        model.missionStatement?.description.length > 0 ? (
          <StatementSection
            mode="view"
            model={model.missionStatement}
            isMissionStatement
          />
        ) : null}
        {shouldDisplayFeaturedSection ? (
          <FeaturedSection mode="view" model={model.featured} />
        ) : null}
        {shouldDisplayAwardsSection ? (
          <AwardsSection mode="view" model={model.awards} />
        ) : null}
        {model.gallery && model.gallery.photos.length > 0 ? (
          <GallerySection
            mode="view"
            model={model.gallery}
            link={localize({
              path: `/${ROOT_PATH}/${urlParams.companySlug}/${CompanyProfilesPage.Culture}${isDraftLink}`,
              language,
            })}
            onActiveTabChanged={onActiveTabChanged}
            trackSeeAllPhotosButtonClicked={trackSeeAllPhotosButtonClicked}
          />
        ) : null}
        {model?.reviews && (
          <ReviewsSummarySectionWithDetails
            model={model}
            link={seeAllReviewsLink}
            onActiveTabChanged={onActiveTabChanged}
            trackReviewsClicked={trackReviewsClicked}
            trackWriteAReviewClicked={trackWriteAReviewClicked}
            shouldDisplayAiGenSection={shouldDisplayAiGenSection}
          />
        )}
        <SeoCtaBlurb
          companyName={model.name}
          getCompanyProfilesPageUrl={getCompanyProfilesPageUrl}
          onActiveTabChanged={onActiveTabChanged}
        />
      </Stack>
      <Hidden below="tablet">
        <Section>
          <CompaniesList
            title={t('Other companies in this industry')}
            allCompanies={model.randomCompaniesInThisIndustry}
            trackCompanyProfileLinkClicked={
              trackRandomCompanyProfileLinkClicked
            }
          />
        </Section>
      </Hidden>
    </>
  );
}
